import Grid from '@material-ui/core/Grid';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import React, { useEffect, useMemo, useReducer } from 'react';
import { parseISO } from 'date-fns';
import AccountList from '../account-summary/multi-accounts/AccountList';
import SortFilterPageHeader from '../account-summary/sort-filter-page-header/SortFilterPageHeader';
import useAccountDetailList from '../../hooks/useAccountDetailList';
import { getAccountsList } from './queries';
import { useTranslation } from '../../hooks/useTranslation';
import useSelectedGroupId from '../../hooks/useSelectedGroupId';
import useSelectedAccountParams from '../../hooks/useSelectedAccountParams';
import AccountListReducer from '../account-summary/multi-accounts/accountListReducer';
import Backdrop from '../backdrop';
import colors from '../../themes/main-colors';
import useAccountMulticheckState from '../../hooks/useAccountMulticheckState';
import Box from '@material-ui/core/Box';
import { AccountDetail } from '../../__generated__/pge-types';
import { navigate } from '@reach/router';
import ROUTES from '../../routes';
import Alerts from '../alerts';
import { SelectAccountsState } from './types';
import { toDateStringFullMonthName } from '../../util/format';
import useAccountListHeader from '../account-summary/multi-accounts/useAccountListHeader';
import PaySelectedAccountsCard from '../account-summary/multi-accounts/PaySelectedAccountsCard';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttonRow: {
      marginTop: '1em',
      display: 'flex',
      justifyContent: 'space-between',
      flexDirection: 'row',
      alignItems: 'center',
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        justifyContent: 'center',
      },
    },
    buttonContainer: {
      display: 'flex',
      marginLeft: '4px',
      [theme.breakpoints.down('md')]: {
        marginLeft: '0',
        marginTop: '1em',
        width: '100%',
        justifyContent: 'center',
      },
    },
    button: {
      background: colors.orange,
      ':hover': {
        background: colors.sparkBlue,
      },
      [theme.breakpoints.down('md')]: {
        width: '80%',
      },
    },
    paySelectedCard: {
      marginTop: 12,
      position: 'relative',
      // Large desktop screens
      '@media (min-width:1536px)': {
        position: 'absolute',
        top: 0,
        right: -320, // ✅ better to use number instead of string
      },
    },
  }),
);

const defaultState: SelectAccountsState = {
  accountListState: AccountListReducer.initialState,
  checkedState: {},
};

type Props = {
  path?: string;
  initialState?: SelectAccountsState;
  onSubmit: (
    accounts: Array<AccountDetail>,
    state: SelectAccountsState,
  ) => void;
};

export default function SelectAccountsStep({
  onSubmit,
  initialState = defaultState,
}: Props) {
  const { t } = useTranslation();
  const { selectedGroupId } = useSelectedGroupId();
  const { setSelectedAccountParams } = useSelectedAccountParams();
  const {
    accountListParams,
    onAccountPageChange,
    onAccountSearch,
    onAccountSort,
  } = useAccountListHeader();

  const {
    loading: hookLoading,
    detailList,
    accounts,
    totalCount = 0,
  } = useAccountDetailList(getAccountsList, selectedGroupId, {
    accountListParams,
  });
  const loading = !detailList || hookLoading;

  const multicheckState = useAccountMulticheckState({
    initialChecked: initialState.checkedState,
    query: accountListParams.query,
  });

  useEffect(() => {
    if (multicheckState.isCurrentChargesError) {
      typeof window !== 'undefined' && window.scrollTo(0, 0);
    }
  }, [multicheckState.isCurrentChargesError]);

  const classes = useStyles();

  const hasError = !loading && multicheckState.options.length === 0;
  const checkedAccountsKey = multicheckState.checkedOptions().join(',');

  // Detect changes in account selection and recalculate the total amount due
  // Use primitive `checkedAccountsKey` instead of `getSelectedAccounts()`
  // to avoid unnecessary recalculations on every render, since
  // `getSelectedAccounts()` returns a new reference each time
  const totalPaymentAmount = useMemo(() => {
    return multicheckState
      .getSelectedAccounts()
      .reduce(
        (sum: number, account: AccountDetail) =>
          sum +
          (account?.currentCharges && account?.currentCharges?.amountDue > 0
            ? account?.currentCharges?.amountDue || 0
            : 0),
        0,
      );
  }, [checkedAccountsKey]);

  return (
    <>
      {(loading || multicheckState.areCurrentChargesLoading) && (
        <Backdrop forceOpen />
      )}

      {!loading && (
        <SortFilterPageHeader
          onAccountSearch={onAccountSearch}
          onAccountSort={onAccountSort}
          onAccountPageChange={onAccountPageChange}
          accountListParams={accountListParams}
          totalCount={totalCount}
        />
      )}

      {hasError && (
        <Box marginTop={2}>
          <Alerts
            disableColorOverride={true}
            isOpen
            severity="error"
            variant="outlined"
            message={t('MULTI_PAY_NO_ACCOUNTS')}
          />
        </Box>
      )}

      {!loading && !hasError && (
        <Box marginTop={2}>
          <Grid item xs={12} style={{ position: 'relative' }}>
            <Grid item className={classes.paySelectedCard}>
              <PaySelectedAccountsCard
                totalPaymentAmount={totalPaymentAmount}
                accountsSelected={multicheckState.checkedOptions().length}
                areAllWithBalanceSelected={
                  multicheckState.areAllWithBalanceSelected
                }
                onSelectAllChange={async checked => {
                  await multicheckState.toggleSelectAllWithBalance(checked);
                }}
                onPaySelected={() =>
                  onSubmit(multicheckState.getSelectedAccounts(), {
                    accountListState: accountListParams,
                    checkedState: multicheckState.checked,
                  })
                }
              />
            </Grid>
            <AccountList
              inactiveText={t('CLOSED')}
              accounts={accounts || []}
              multicheckState={multicheckState}
              onViewDetails={account => {
                setSelectedAccountParams(selectedGroupId!, account);
                return navigate(ROUTES.ACCOUNT);
              }}
              billMenuType="VIEW"
            />
          </Grid>

          <Grid item xs={12} className={classes.buttonRow}>
            <Grid item>
              <Typography variant={'body2'}>
                {detailList?.timestamp &&
                  `${t('AMOUNT_DUE_INCLUDES')} ${toDateStringFullMonthName(
                    parseISO(detailList?.timestamp),
                  )}.`}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
}
